/**
 * Pagination footer. Renders the count summary on the left and the
 * page buttons on the right. Clicking a button calls `onPageChange`
 * (which becomes the React Query key).
 *
 * The button strip is intentionally simple: `«` `‹` `[1] 2 … n` `›` `»`.
 * Greptile caps the visible window to ~5 pages with ellipses; v1 just
 * shows every page when `totalPages <= 7`, otherwise an ellipsised
 * neighbourhood around the current page.
 */
interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

function clampPage(page: number, totalPages: number): number {
  if (totalPages < 1) return 1;
  if (page < 1) return 1;
  if (page > totalPages) return totalPages;
  return page;
}

function pageWindow(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p += 1) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

export function Pagination({ page, pageSize, total, onPageChange }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safe = clampPage(page, totalPages);
  const first = total === 0 ? 0 : (safe - 1) * pageSize + 1;
  const last = Math.min(total, safe * pageSize);
  const items = pageWindow(safe, totalPages);
  if (total === 0) {
    return (
      <footer className="feluda-pagination feluda-pagination-empty">
        <span className="feluda-pagination-summary">0 results</span>
      </footer>
    );
  }
  return (
    <footer className="feluda-pagination">
      <span className="feluda-pagination-summary">
        {first}–{last} of {total}
      </span>
      <nav className="feluda-pagination-nav" aria-label="Pagination">
        <button
          type="button"
          className="feluda-pagination-edge"
          disabled={safe === 1}
          onClick={() => onPageChange(1)}
          aria-label="First page"
        >
          «
        </button>
        <button
          type="button"
          className="feluda-pagination-edge"
          disabled={safe === 1}
          onClick={() => onPageChange(safe - 1)}
          aria-label="Previous page"
        >
          ‹
        </button>
        {items.map((item, idx) =>
          item === '…' ? (
            <span key={`ellipsis-${idx}`} className="feluda-pagination-ellipsis">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={`feluda-pagination-page${
                item === safe ? ' feluda-pagination-page-active' : ''
              }`}
              aria-current={item === safe ? 'page' : undefined}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          className="feluda-pagination-edge"
          disabled={safe === totalPages}
          onClick={() => onPageChange(safe + 1)}
          aria-label="Next page"
        >
          ›
        </button>
        <button
          type="button"
          className="feluda-pagination-edge"
          disabled={safe === totalPages}
          onClick={() => onPageChange(totalPages)}
          aria-label="Last page"
        >
          »
        </button>
      </nav>
    </footer>
  );
}
